import { type FC, useCallback } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { Button } from '@/components/buttons';
import { IconComponent } from '@/components/IconComponent';
import { ImageComponent } from '@/components/Image';
import { Skeleton } from '@/components/Skeleton';
import { useToggle } from '@/hooks/common';
import { useNavigation } from '@/hooks/navigation';
import { ROLLS_PER_DAY, useDiceRoll } from '@/modules/promotions';
import { useGetCommonStore } from '@/store';
import { ScFortuneWheelPromoSection } from '../FortuneWheelPromoSection/styled';

export interface DiceRollPromoSectionProps {
  onLinkClick?: () => void;
}

export const DiceRollPromoSection: FC<DiceRollPromoSectionProps> = ({
  onLinkClick,
}) => {
  const { navigate } = useNavigation();
  const useCommonStore = useGetCommonStore();

  const [isFullInfoOpen, toggleFullInfo] = useToggle();

  const { isDailyRollsLimit, rollsLeftAmount } = useDiceRoll();

  const { diceRollMenuData, pendingDiceRollMenuData } = useCommonStore(
    useShallow((state) => ({
      diceRollMenuData: state.diceRollMenuData,
      pendingDiceRollMenuData: state.pendingDiceRollMenuData,
    }))
  );

  const handleNavigateToDiceRoll = useCallback(() => {
    navigate('/dice-roll');
  }, [navigate]);

  if (pendingDiceRollMenuData) return <Skeleton.Rect height={84} />;

  if (!(diceRollMenuData && diceRollMenuData?.menu)) return null;

  return (
    <ScFortuneWheelPromoSection
      onClick={toggleFullInfo}
      showFullInfo={isFullInfoOpen}
    >
      <div className="info">
        <div className="level-info">
          <div className="text">
            <span>
              <b className="reward">{diceRollMenuData?.menu.reward}</b>
            </span>
            {isDailyRollsLimit ? (
              <span>{diceRollMenuData?.menu.tryTomorrow}</span>
            ) : (
              <span>
                <b>{diceRollMenuData?.menu.dailySpins}</b>
                {`${rollsLeftAmount}/${ROLLS_PER_DAY}`}
              </span>
            )}
          </div>
          <div className="addons">
            <ImageComponent
              alt="character"
              height={44}
              src={diceRollMenuData?.menu?.character?.url || ''}
              width={44}
            />
            <IconComponent name="whiteArrowRight" size="18px" />
          </div>
        </div>
      </div>
      {isFullInfoOpen && (
        <Button
          navigation
          onClick={() => {
            onLinkClick && onLinkClick();
            handleNavigateToDiceRoll();
          }}
        >
          {diceRollMenuData.pageTitle}
        </Button>
      )}
    </ScFortuneWheelPromoSection>
  );
};
